// General Sober Living / Recovery Pages
const pages = [
  {
    slug: '/recovery-home/',
    category: 'General Sober Living / Recovery', tier: 'Tier 2',
    name: 'Recovery Home',
    primaryKeyword: 'Recovery Home',
    keywords: ['recovery house', 'recovery homes', 'recovery home near me'],
    title: 'Recovery Home for Men & Women | PGSRH Lehigh Valley, PA',
    description: 'A recovery home is a structured, sober place to live with peers in recovery. PGSRH operates recovery homes for men and women in the Lehigh Valley, PA. Apply today.',
    blurb: 'What a recovery home is and where PGSRH homes are located.',
    heroSubtitle: 'A recovery home is a substance-free household of peers in recovery \u2014 structure, support, and accountability under one roof.',
    intro: [
      'Many people use the phrase "recovery home" for what a professional might call a recovery residence or sober living home. It all points to the same thing: a safe, substance-free place to live with others who are committed to recovery. PGSRH operates recovery homes for men and women across the Lehigh Valley.',
      'Whether you searched for a recovery home, a recovery house, or sober living, you are in the right place \u2014 this page explains what our homes are and how to explore each location.',
    ],
    whoFor: [
      'People who want a sober, structured place to call home',
      'Individuals leaving treatment or an unstable environment',
      'Residents who value peer support and daily accountability',
      'Families looking for a recovery home for a loved one',
    ],
    sections: [
      { h: 'What makes a recovery home work', p: [
        'A recovery home works because of the people in it. Residents share responsibilities, keep the home substance-free, and hold one another to shared expectations. That combination of community and structure is hard to recreate alone.',
        'Each PGSRH recovery home is located in the Lehigh Valley, close to meetings, jobs, and family.',
      ] },
      { h: 'Explore our recovery homes', p: [
        'We operate men\u2019s recovery homes in Whitehall and a women\u2019s recovery home in Catasauqua. Visit the individual city pages to see each location and what living there involves.',
        'If you are not sure which home fits, reach out and we\u2019ll help you weigh location and current availability.',
      ] },
    ],
    faqs: [
      { q: 'Is a recovery home the same as sober living?', a: 'Yes. "Recovery home," "recovery house," "recovery residence," and "sober living" all describe a structured, substance-free home for people in recovery.' },
      { q: 'Do recovery homes provide treatment?', a: 'No. A recovery home is housing with peer support, not clinical treatment. PGSRH does not provide medical services.' },
      { q: 'Where are PGSRH recovery homes?', a: 'In the Lehigh Valley: Whitehall for men, and Catasauqua for women.' },
      { q: 'How do I apply for a recovery home?', a: 'Apply online or call PGSRH. We\u2019ll explain current availability and the move-in process.' },
    ],
    funding: false,
  },
  {
    slug: '/recovery-homes-near-me/',
    category: 'General Sober Living / Recovery', tier: 'Tier 2',
    name: 'Recovery Homes Near Me',
    primaryKeyword: 'Recovery Homes Near Me',
    keywords: ['recovery houses near me', 'recovery home near me', 'sober homes near me'],
    title: 'Recovery Homes Near Me \u2013 Lehigh Valley, PA | PGSRH',
    description: 'Searching for recovery homes near you? PGSRH operates sober, structured recovery homes for men and women in Whitehall and Catasauqua, PA.',
    blurb: 'Nearby recovery homes across the Lehigh Valley service area.',
    heroSubtitle: 'Recovery homes near you in the Lehigh Valley \u2014 close to your meetings, your job, and the people who support you.',
    intro: [
      'When you search for "recovery homes near me," you want something local and dependable. PGSRH recovery homes are located throughout the Lehigh Valley, serving Allentown, Bethlehem, Easton, and nearby towns.',
      'Staying near your own support system \u2014 meetings, work, and family \u2014 is one of the biggest advantages of a nearby recovery home.',
    ],
    whoFor: [
      'People who want a recovery home close to home or work',
      'Individuals who need to stay near local meetings and providers',
      'Families searching locally in the Lehigh Valley',
      'Anyone leaving treatment who needs a nearby, stable place to live',
    ],
    sections: [
      { h: 'Our service area', p: [
        'PGSRH recovery homes are in Whitehall and Catasauqua \u2014 all within the greater Lehigh Valley. Residents come from Allentown, Bethlehem, Easton, and surrounding communities.',
        'Because our homes are local, you can keep the routines and relationships that support your recovery.',
      ] },
      { h: 'Finding the closest fit', p: [
        'Decide whether you need men\u2019s or women\u2019s housing, then compare the city pages to find the location that keeps you closest to your support network.',
        'Call us or apply online and we\u2019ll tell you what is currently open nearby.',
      ] },
    ],
    faqs: [
      { q: 'Where are your recovery homes located?', a: 'In Whitehall and Catasauqua, PA \u2014 all in the Lehigh Valley.' },
      { q: 'Do you serve my town?', a: 'We serve the greater Lehigh Valley, including Allentown, Bethlehem, Easton, and nearby areas. Contact us to confirm.' },
      { q: 'How quickly can I move into a nearby home?', a: 'Availability changes often. Contact PGSRH for current openings and timing.' },
      { q: 'Is there nearby help with cost?', a: 'Funding or scholarship options may be available depending on eligibility and availability. Ask PGSRH what may apply.' },
    ],
    funding: false,
  },
  {
    slug: '/recovery-living/',
    category: 'General Sober Living / Recovery', tier: 'Tier 2',
    name: 'Recovery Living',
    primaryKeyword: 'Recovery Living',
    keywords: ['recovery living homes', 'recovery living program', 'sober recovery living'],
    title: 'Recovery Living | Daily Life in a PGSRH Home | Lehigh Valley PA',
    description: 'Recovery living means building healthy daily routines in a supportive, sober home. See what recovery living looks like day to day at PGSRH in the Lehigh Valley, PA.',
    blurb: 'What daily recovery living looks like inside a PGSRH home.',
    heroSubtitle: 'Recovery living is the day-to-day: routine, responsibility, and community in a supportive, substance-free home.',
    intro: [
      '"Recovery living" describes the everyday experience of living in a recovery home \u2014 the routines, responsibilities, and relationships that support a sober life. PGSRH homes are designed to make that daily experience steady and supportive.',
      'This page focuses on what life actually looks like: what a normal day involves, how residents support one another, and how those ordinary habits add up to lasting change.',
    ],
    whoFor: [
      'People who want to understand daily life in recovery housing',
      'Individuals deciding whether structured living fits them',
      'Families picturing what a loved one\u2019s day would look like',
      'Anyone rebuilding routine and responsibility in recovery',
    ],
    sections: [
      { h: 'A typical day', p: [
        'Recovery living looks a lot like healthy everyday life: getting up on time, going to work or looking for it, attending meetings, handling chores, and being present for the household. The difference is the built-in support and accountability.',
        'Small, consistent habits \u2014 kept up alongside peers \u2014 are what make recovery durable.',
      ] },
      { h: 'Community and accountability', p: [
        'Residents look out for one another. Living with people who share the same goals means you are less likely to slip through the cracks and more likely to be encouraged when things get hard.',
        'For more, see our resources on the first week in a recovery house and the rules of a recovery home.',
      ] },
    ],
    faqs: [
      { q: 'What does recovery living involve day to day?', a: 'Routine and responsibility: work or job searching, meetings, chores, and being an accountable member of the household, all in a substance-free home.' },
      { q: 'Is recovery living a program with an end date?', a: 'It is a way of living in a recovery home. Length of stay is flexible and based on individual needs.' },
      { q: 'Does recovery living include therapy?', a: 'No. PGSRH provides housing and peer support, not clinical treatment. Residents use their own outside providers for clinical care.' },
      { q: 'How do I start?', a: 'Apply online or call PGSRH to ask about availability and next steps.' },
    ],
    funding: false,
  },
  {
    slug: '/recovery-house-pennsylvania/',
    category: 'General Sober Living / Recovery', tier: 'Tier 2',
    name: 'Recovery House Pennsylvania',
    primaryKeyword: 'Recovery House Pennsylvania',
    keywords: ['recovery house PA', 'recovery homes Pennsylvania', 'sober house Pennsylvania', 'state funded recovery house PA'],
    title: 'Recovery House Pennsylvania | PGSRH Lehigh Valley',
    description: 'PGSRH operates recovery houses in Pennsylvania for men and women. Structured, sober homes in the Lehigh Valley. Explore locations and ask about availability.',
    blurb: 'Pennsylvania recovery houses connecting to PGSRH locations.',
    heroSubtitle: 'Recovery houses in Pennsylvania for men and women \u2014 structured, sober homes located in the Lehigh Valley.',
    intro: [
      'People throughout Pennsylvania look for a recovery house \u2014 a sober, structured home to live in during recovery. PGSRH provides recovery houses for men and women in the Lehigh Valley, operating as DDAP Licensed Recovery Residences.',
      'This page connects Pennsylvania recovery house searches to our specific homes in Whitehall and Catasauqua.',
    ],
    whoFor: [
      'Pennsylvania residents seeking a recovery house',
      'People relocating within PA for a supportive, sober home',
      'Families researching recovery houses in the state',
      'Referral partners arranging placement in the Lehigh Valley',
    ],
    sections: [
      { h: 'Recovery houses in the Lehigh Valley', p: [
        'Our recovery houses provide a substance-free environment, house expectations, and peer accountability. Men\u2019s houses are in Whitehall; a women\u2019s house is in Catasauqua.',
        'PGSRH operates as DDAP Licensed Recovery Residences \u2014 language that describes our housing and does not guarantee funding or placement.',
      ] },
      { h: 'How to explore your options', p: [
        'Visit each city page to see the individual houses, or use our gender-specific hubs to compare men\u2019s and women\u2019s options.',
        'Our funding section explains, in careful terms, how cost and possible assistance may work.',
      ] },
    ],
    faqs: [
      { q: 'Where are your recovery houses in Pennsylvania?', a: 'In the Lehigh Valley: Whitehall for men, and Catasauqua for women.' },
      { q: 'Is a recovery house treatment?', a: 'No. A recovery house is sober, structured housing with peer support, not clinical treatment.' },
      { q: 'Are these licensed?', a: 'PGSRH operates as DDAP Licensed Recovery Residences, which describes our housing and does not guarantee funding or placement.' },
      { q: 'Is state or county funding available?', a: 'County, state, scholarship, or assistance programs may be available depending on eligibility and availability. Ask PGSRH what may apply.' },
    ],
    funding: true,
  },
  {
    slug: '/sober-living-pennsylvania/',
    category: 'General Sober Living / Recovery', tier: 'Tier 2',
    name: 'Sober Living Pennsylvania',
    primaryKeyword: 'Sober Living Pennsylvania',
    keywords: ['sober living PA', 'sober living homes Pennsylvania', 'sober housing Pennsylvania', 'state funded sober living PA'],
    title: 'Sober Living Pennsylvania | PGSRH Lehigh Valley Homes',
    description: 'PGSRH sober living in Pennsylvania offers structured, substance-free homes for men and women in the Lehigh Valley. Explore locations and ask about availability.',
    blurb: 'Statewide sober living linking to PGSRH Lehigh Valley homes.',
    heroSubtitle: 'Sober living in Pennsylvania for men and women \u2014 structured, substance-free homes in the Lehigh Valley.',
    intro: [
      'Sober living in Pennsylvania gives people in recovery a substance-free home and a supportive community. PGSRH offers sober living for men and women in the Lehigh Valley, with homes in Whitehall and Catasauqua.',
      'This page serves as a state-level hub, connecting Pennsylvania sober living searches to our specific locations.',
    ],
    whoFor: [
      'Pennsylvania residents seeking sober living',
      'People relocating within PA for a sober, structured home',
      'Families researching sober living in the state',
      'Referral partners placing clients in the Lehigh Valley',
    ],
    sections: [
      { h: 'Sober living across the Lehigh Valley', p: [
        'Though the search is statewide, PGSRH homes are concentrated in the Lehigh Valley, keeping residents close to meetings, jobs, and family. Men\u2019s homes are in Whitehall; a women\u2019s home is in Catasauqua.',
        'Each home is substance-free with clear expectations and strong peer support.',
      ] },
      { h: 'Choosing a home', p: [
        'Start with men\u2019s or women\u2019s sober living, then compare city pages to find the location closest to your support network.',
        'Contact us to learn what is open now and how the application works.',
      ] },
    ],
    faqs: [
      { q: 'Where is your sober living in Pennsylvania?', a: 'In the Lehigh Valley: Whitehall for men, and Catasauqua for women.' },
      { q: 'Is sober living the same as recovery housing?', a: 'Yes. At PGSRH the terms describe the same structured, substance-free housing with peer support.' },
      { q: 'Does sober living include treatment?', a: 'No. It is housing only. PGSRH does not provide clinical or medical services.' },
      { q: 'Is there funding for sober living in PA?', a: 'County, state, scholarship, or assistance options may be available depending on eligibility and availability. Ask PGSRH what may apply.' },
    ],
    funding: true,
  },
];

export default pages;
